import React from "react";

function Section({ title, children }) {
  return (
    <section className="mb-10">
      <h2 className="font-display text-2xl md:text-3xl font-bold mb-4">
        {title}
      </h2>
      <div className="text-white/70 leading-relaxed space-y-3">{children}</div>
    </section>
  );
}

export default function PrivacyPolicy() {
  return (
    <main className="relative pt-40 pb-24">
      <div className="aurora-orb bg-[#0047FF] w-[500px] h-[500px] -top-20 -left-40 opacity-30" />
      <div className="container-x relative max-w-4xl">
        <div className="text-xs uppercase tracking-[0.25em] text-[#00F0FF] mb-4">
          Legal
        </div>
        <h1 className="font-display text-5xl md:text-6xl font-bold mb-3">
          Privacy <span className="aurora-text">Policy</span>
        </h1>
        <p className="text-white/50 mb-12">Last Updated: March 27, 2026</p>

        <p className="text-white/70 leading-relaxed mb-10">
          MonetiseUP ("we," "our," or "us") is committed to protecting the
          privacy of our users. This Privacy Policy explains how we collect,
          use, disclose, and safeguard your information when you visit our
          website monetiseup.com and use our ad-tech platform and services
          (collectively, the "Services").
        </p>

        <Section title="1. Information We Collect">
          <p>We may collect information about you in various ways:</p>
          <h3 className="font-display text-lg text-white mt-4">a. Personal Data</h3>
          <p>
            When you register for an account, contact us, or use certain
            features of our Services, we may collect personally identifiable
            information, such as your name, email address, company name, phone
            number, and payment information.
          </p>
          <h3 className="font-display text-lg text-white mt-4">b. Usage Data</h3>
          <p>
            We automatically collect certain information when you access or use
            our Services, including your IP address, browser type, operating
            system, referral URLs, pages viewed, time spent on pages, and other
            usage statistics. For our ad-tech platform, this may include ad
            impressions, clicks, conversions, and device identifiers.
          </p>
          <h3 className="font-display text-lg text-white mt-4">
            c. Cookies and Tracking Technologies
          </h3>
          <p>
            We use cookies and similar tracking technologies (like pixels and
            web beacons) to track activity on our Services and hold certain
            information. These are used for purposes such as remembering your
            preferences, analyzing traffic, and delivering targeted advertising.
            You can control cookie preferences through your browser settings.
          </p>
        </Section>

        <Section title="2. How We Use Your Information">
          <p>We use the information we collect for various purposes, including to:</p>
          <ul className="list-disc list-inside space-y-1.5 marker:text-[#00F0FF]">
            <li>Provide, operate, and maintain our Services.</li>
            <li>Process your transactions and manage your account.</li>
            <li>Improve, personalize, and expand our Services.</li>
            <li>Understand and analyze how you use our Services.</li>
            <li>Develop new products, services, features, and functionality.</li>
            <li>Communicate with you, including for customer service, updates, and marketing.</li>
            <li>Detect and prevent fraud and other illegal activities.</li>
            <li>Comply with legal obligations.</li>
            <li>Deliver relevant advertisements and measure their effectiveness.</li>
          </ul>
        </Section>

        <Section title="3. How We Share Your Information">
          <p>We may share your information with third parties in the following situations:</p>
          <ul className="list-disc list-inside space-y-1.5 marker:text-[#00F0FF]">
            <li><strong className="text-white">Service Providers:</strong> We may share your information with third-party vendors, consultants, and other service providers who perform services on our behalf, such as payment processing, data analysis, email delivery, hosting services, and customer service.</li>
            <li><strong className="text-white">Business Transfers:</strong> In connection with any merger, sale of company assets, financing, or acquisition of all or a portion of our business by another company.</li>
            <li><strong className="text-white">Affiliates:</strong> We may share your information with our affiliates, in which case we will require those affiliates to honor this Privacy Policy.</li>
            <li><strong className="text-white">Legal Requirements:</strong> We may disclose your information if required to do so by law or in response to valid requests by public authorities (e.g., a court order or government agency).</li>
            <li><strong className="text-white">Advertisers and Publishers:</strong> For the purpose of facilitating advertising campaigns and reporting, aggregated or anonymized data may be shared with our advertising and publishing partners.</li>
          </ul>
        </Section>

        <Section title="4. Data Security">
          <p>
            We implement reasonable technical and organizational measures
            designed to protect your personal data from unauthorized access,
            use, alteration, and disclosure. However, no method of transmission
            over the Internet or method of electronic storage is 100% secure.
          </p>
        </Section>

        <Section title="5. Your Choices and Rights">
          <ul className="list-disc list-inside space-y-1.5 marker:text-[#00F0FF]">
            <li><strong className="text-white">Account Information:</strong> You may update or correct your account information at any time by logging into your account settings.</li>
            <li><strong className="text-white">Marketing Communications:</strong> You can opt-out of receiving promotional emails from us by following the unsubscribe instructions in those emails.</li>
            <li><strong className="text-white">Do Not Track:</strong> Some web browsers may transmit "do not track" signals. We do not currently respond to these signals.</li>
            <li><strong className="text-white">Data Subject Rights:</strong> Depending on your jurisdiction, you may have rights regarding your personal data, such as the right to access, correct, delete, or restrict its processing. Please contact us to exercise these rights.</li>
          </ul>
        </Section>

        <Section title="6. Third-Party Links">
          <p>
            Our Services may contain links to third-party websites or services
            that are not owned or controlled by MonetiseUP. We are not
            responsible for the privacy practices or content of these third-party
            sites. We encourage you to review the privacy policies of any
            third-party sites you visit.
          </p>
        </Section>

        <Section title="7. Changes to This Privacy Policy">
          <p>
            We may update our Privacy Policy from time to time. You are advised
            to review this Privacy Policy periodically for any changes.
          </p>
        </Section>

        <Section title="8. Contact Us">
          <p>
            If you have any questions about this Privacy Policy, please contact
            us at{" "}
            <a
              className="text-[#00F0FF] hover:underline"
              href="mailto:contact@monetiseup.com"
            >
              contact@monetiseup.com
            </a>
            .
          </p>
        </Section>
      </div>
    </main>
  );
}
